import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import api from '@/utils/apiClient';

// Display configuration - Now fetched from backend
const DEFAULT_ITEM_DISPLAY_DURATION = 5000; // 5 seconds per item (can be overridden by backend)
const AD_DISPLAY_DURATION = 5000; // 5 seconds per ad
const REFRESH_INTERVAL = 30000; // 30 seconds to refresh inventory data

// Display modes - NO SPLASH, only inventory and ads
const MODE_INVENTORY = 'inventory';
const MODE_ADS = 'ads';
const MODE_CLOSED = 'closed';  // New: Closed hours mode

// Urgency color thresholds - Return quantity separately for animation
// Now includes blinking background animation classes
const getUrgencyColor = (quantity) => {
  if (quantity <= 0) return { 
    bg: 'bg-gray-600', 
    text: 'text-gray-400', 
    glow: 'shadow-gray-500/50', 
    qty: 0,
    isLowStock: false,
    isSoldOut: true,
    blinkClass: ''
  };
  if (quantity <= 3) return { 
    bg: '', // Will use animated background
    text: 'text-red-500', 
    glow: 'shadow-red-500/50', 
    qty: quantity, 
    isLowStock: true,
    isSoldOut: false,
    blinkClass: 'animate-blink-urgent' // Fast red↔orange
  };
  return { 
    bg: '', // Will use animated background
    text: 'text-emerald-400', 
    glow: 'shadow-emerald-500/50', 
    qty: quantity, 
    isLowStock: false,
    isSoldOut: false,
    blinkClass: 'animate-blink-normal' // Slow green↔blue
  };
};

// Category display names
const CATEGORY_NAMES = {
  'muffins': 'FRESH BAKED MUFFINS',
  'pastries': 'FRESH BAKED PASTRIES',
  'pies': 'FRESH BAKED PIES',
  'sweets': 'HOUSEMADE SWEETS',
  'grab-and-go': 'GRAB & GO',
  'breads': 'FRESH BAKED BREADS',
  'breakfast': 'BREAKFAST',
  'lunch': 'LUNCH'
};

// Category-specific placeholder icons
const CATEGORY_ICONS = {
  'muffins': '🧁',
  'pastries': '🥐',
  'pies': '🥧',
  'sweets': '🍰',
  'grab-and-go': '🥪',
  'breads': '🍞',
  'breakfast': '🍳',
  'lunch': '🍽️'
};

// Advertisement data - NO URLs, just CTA
const ADVERTISEMENTS = [
  {
    id: 'menu',
    title: 'VIEW OUR FULL MENU',
    subtitle: 'Order online for pickup',
    description: 'Fresh food & drinks',
    cta: 'Come Inside For More Details',
    bgGradient: 'from-cyan-800 via-cyan-900 to-black',
    icon: '📋',
    accentColor: '#06b6d4'
  },
  {
    id: 'coffee_pass',
    title: 'BULA COFFEE PASS',
    subtitle: 'Unlimited coffee for $95/year',
    description: 'Your daily coffee, covered',
    cta: 'Come Inside For More Details',
    bgGradient: 'from-amber-800 via-amber-900 to-black',
    icon: '☕',
    accentColor: '#f59e0b'
  },
  {
    id: 'rewards',
    title: 'JOIN BULACCINO REWARDS',
    subtitle: 'Earn points on every order!',
    description: 'Up to 20% off for life',
    cta: 'Come Inside For More Details',
    bgGradient: 'from-emerald-800 via-emerald-900 to-black',
    icon: '🎁',
    accentColor: '#10b981'
  }
];

const BakedGoodsDisplay = () => {
  const { displayNumber: paramDisplayNumber } = useParams();
  const location = useLocation();
  
  // Extract display number from URL path or params
  const getDisplayNumber = () => {
    if (paramDisplayNumber) return parseInt(paramDisplayNumber);
    const match = location.pathname.match(/display(\d+)/);
    if (match) return parseInt(match[1]);
    return 1;
  };
  
  const displayNum = getDisplayNumber();
  
  // State - Start directly in INVENTORY mode (NO splash screens)
  const [mode, setMode] = useState(MODE_INVENTORY);
  const [inventory, setInventory] = useState([]);
  const [displayName, setDisplayName] = useState(`Window Display #${displayNum}`);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startOffset, setStartOffset] = useState(0);
  const [currentCategory, setCurrentCategory] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [animationKey, setAnimationKey] = useState(0);
  const [isClosedHours, setIsClosedHours] = useState(false);
  const [itemDuration, setItemDuration] = useState(DEFAULT_ITEM_DISPLAY_DURATION);
  const [fijiTime, setFijiTime] = useState('');

  // Fetch inventory data
  const fetchInventory = useCallback(async () => {
    try {
      const response = await api.get(`/display/inventory/${displayNum}`);
      const data = response.data;
      
      // Check if we're in closed hours
      if (data.is_closed_hours) {
        setIsClosedHours(true);
        setMode(MODE_CLOSED);
      } else {
        setIsClosedHours(false);
        if (mode === MODE_CLOSED) {
          setMode(MODE_INVENTORY);
        }
      }
      
      const activeItems = data.items.filter(item => item.is_active);
      setInventory(activeItems);
      setDisplayName(data.display_name);
      setFijiTime(data.fiji_time || '');
      
      // Set item duration from backend (in seconds, convert to ms)
      if (data.item_duration) {
        setItemDuration(data.item_duration * 1000);
      }
      
      // Set start offset from backend - each display starts at different position
      const offset = data.start_offset || 0;
      setStartOffset(offset);
      
      // On first load, set current index based on offset (wrapped)
      if (isLoading && activeItems.length > 0) {
        const initialIndex = offset % activeItems.length;
        setCurrentIndex(initialIndex);
        setCurrentCategory(activeItems[initialIndex]?.category || '');
      } else if (activeItems.length > 0 && !currentCategory) {
        setCurrentCategory(activeItems[0].category);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      setIsLoading(false);
    }
  }, [displayNum, currentCategory, isLoading, mode]);

  // Initial fetch - NO splash assets needed
  useEffect(() => {
    fetchInventory();
    const refreshTimer = setInterval(fetchInventory, REFRESH_INTERVAL);
    return () => clearInterval(refreshTimer);
  }, [fetchInventory]);

  // Main rotation logic - INVENTORY then ADS, no splash, handle closed hours
  useEffect(() => {
    if (isLoading) return;
    
    // If closed hours, don't rotate
    if (mode === MODE_CLOSED) return;
    
    const getDuration = () => {
      return mode === MODE_ADS ? AD_DISPLAY_DURATION : itemDuration;
    };

    const rotationTimer = setInterval(() => {
      setAnimationKey(prev => prev + 1);
      
      if (mode === MODE_INVENTORY) {
        // Cycle through inventory items
        if (currentIndex >= inventory.length - 1) {
          // After all items, show ads
          setMode(MODE_ADS);
          setCurrentIndex(0);
        } else {
          setCurrentIndex(prev => prev + 1);
        }
      } else if (mode === MODE_ADS) {
        // Cycle through ads
        if (currentIndex >= ADVERTISEMENTS.length - 1) {
          // After ads, back to inventory
          setMode(MODE_INVENTORY);
          setCurrentIndex(0);
        } else {
          setCurrentIndex(prev => prev + 1);
        }
      }
    }, getDuration());

    return () => clearInterval(rotationTimer);
  }, [mode, currentIndex, inventory.length, isLoading, itemDuration]);

  // Update current category when in inventory mode
  useEffect(() => {
    if (mode === MODE_INVENTORY && inventory.length > 0) {
      const currentItem = inventory[currentIndex];
      if (currentItem) {
        setCurrentCategory(currentItem.category);
      }
    }
  }, [currentIndex, inventory, mode]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-amber-400 text-3xl font-light tracking-widest">LOADING...</p>
        </div>
      </div>
    );
  }

  // ========== ADS MODE ==========
  if (mode === MODE_ADS) {
    const ad = ADVERTISEMENTS[currentIndex];
    if (!ad) {
      setMode(MODE_INVENTORY);
      setCurrentIndex(0);
      return null;
    }
    
    return (
      <div 
        key={`ad-${animationKey}`}
        className={`min-h-screen bg-gradient-to-br ${ad.bgGradient} flex items-center justify-center overflow-hidden`}
      >
        <div className="flex items-center gap-16 p-12 animate-fade-in">
          <div className="text-[200px] animate-float">{ad.icon}</div>
          
          <div className="text-left">
            <h1 
              className="text-7xl font-black text-white mb-4 tracking-tight animate-slide-right"
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              {ad.title}
            </h1>
            <p className="text-4xl text-white/90 mb-2 animate-slide-right" style={{ animationDelay: '0.1s' }}>
              {ad.subtitle}
            </p>
            <p className="text-2xl text-white/60 mb-8 animate-slide-right" style={{ animationDelay: '0.2s' }}>
              {ad.description}
            </p>
            <div 
              className="inline-block px-8 py-4 rounded-2xl text-3xl font-bold tracking-wider animate-slide-right"
              style={{ backgroundColor: ad.accentColor, color: '#000', animationDelay: '0.3s' }}
            >
              {ad.cta || 'Come Inside For More Details'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== CLOSED HOURS MODE ==========
  if (mode === MODE_CLOSED || isClosedHours) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-black to-slate-950 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          {/* Closed Icon */}
          <div className="text-[150px] mb-8 opacity-80">🌙</div>
          
          {/* Main Message */}
          <h1 
            className="text-7xl font-black text-white mb-6 tracking-tight"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            We Are Closed
          </h1>
          
          {/* Divider */}
          <div className="w-48 h-1 bg-amber-500 mx-auto mb-8 rounded-full"></div>
          
          {/* Re-open Message */}
          <p className="text-4xl text-amber-400 tracking-wide mb-4">
            We Open Again At
          </p>
          <p className="text-8xl font-black text-white tracking-wider mb-12">
            7:00 AM
          </p>
          <p className="text-3xl text-slate-400">
            Tomorrow
          </p>
          
          {/* Footer */}
          <div className="mt-16">
            <p className="text-2xl text-slate-500 tracking-widest">BULACCINO CAFE</p>
          </div>
        </div>
      </div>
    );
  }

  // ========== INVENTORY MODE ==========
  // No items state
  if (inventory.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-950 via-black to-amber-950 flex items-center justify-center">
        <div className="text-center">
          <div className="text-9xl mb-8 animate-bounce">🥐</div>
          <h1 className="text-6xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            FRESH BAKED GOODS
          </h1>
          <p className="text-3xl text-amber-400 tracking-widest">COMING SOON TODAY</p>
        </div>
      </div>
    );
  }

  const currentItem = inventory[currentIndex];
  
  if (!currentItem) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  const urgency = getUrgencyColor(currentItem.current_quantity);
  const categoryName = CATEGORY_NAMES[currentItem.category] || currentItem.category.toUpperCase();

  return (
    <div 
      key={`item-${animationKey}`}
      className="h-screen bg-gradient-to-br from-amber-950 via-black to-amber-950 flex flex-col overflow-hidden"
    >
      {/* Category Header */}
      <header className="py-4 px-12 shrink-0">
        <h2 
          className="text-6xl font-black text-amber-400 tracking-[0.2em] animate-fade-in text-center"
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
        >
          {categoryName}
        </h2>
      </header>

      {/* Main Content - Landscape Split Layout */}
      <div className="flex-1 flex items-center justify-center px-12 gap-10 min-h-0">
        
        {/* Left: Item Image/Video */}
        <div className="relative w-[38%] h-[85%] max-h-[500px] rounded-3xl overflow-hidden shadow-2xl animate-scale-in bg-black">
          {currentItem.is_sold_out ? (
            <div className="w-full h-full bg-gray-900 flex items-center justify-center">
              <div className="text-center">
                <div className="text-8xl mb-4 grayscale opacity-40">{CATEGORY_ICONS[currentItem.category] || '🥐'}</div>
                <p className="text-5xl font-black text-gray-500 tracking-wider">SOLD OUT</p>
              </div>
            </div>
          ) : currentItem.video_url ? (
            <>
              <video
                key={`video-${currentItem.item_id}-${animationKey}`}
                src={currentItem.video_url}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
            </>
          ) : currentItem.image_url ? (
            <>
              <img 
                src={currentItem.image_url} 
                alt={currentItem.name}
                className="w-full h-full object-cover animate-slow-zoom"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-amber-700 via-amber-800 to-amber-900 flex items-center justify-center">
              <div className="text-center animate-pulse-slow">
                <div className="text-[150px]">{CATEGORY_ICONS[currentItem.category] || '🥐'}</div>
              </div>
            </div>
          )}
          
          {currentItem.is_marked_down && !currentItem.is_sold_out && (
            <div className="absolute top-6 left-6 bg-red-600 text-white px-6 py-3 rounded-full font-black text-2xl shadow-xl animate-bounce-slow">
              LIMITED!
            </div>
          )}
        </div>

        {/* Right: Item Details - Centered */}
        <div className="w-[45%] flex flex-col justify-center items-center text-center">
          <h1 
            className="text-6xl font-black text-white mb-4 leading-tight animate-slide-right"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            {currentItem.name}
          </h1>

          <div className="flex items-baseline justify-center gap-4 mb-6 animate-slide-right" style={{ animationDelay: '0.1s' }}>
            {currentItem.is_marked_down ? (
              <>
                <span className="text-5xl text-white/40 line-through font-light">
                  {Math.floor(currentItem.original_price)}
                </span>
                <span className="text-9xl font-black text-emerald-400">
                  {Math.floor(currentItem.effective_price)}
                </span>
              </>
            ) : (
              <span className="text-9xl font-black text-white">
                {Math.floor(currentItem.effective_price)}
              </span>
            )}
          </div>

          {/* Selling Fast Badge - Shows when 3 or fewer remaining (only for items with quantity) */}
          {!currentItem.hide_quantity && urgency.isLowStock && !currentItem.is_sold_out && (
            <div className="mb-6 animate-slide-right animate-selling-fast" style={{ animationDelay: '0.15s' }}>
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-full font-black text-3xl shadow-xl">
                <span className="text-4xl">🔥</span> SELLING FAST!
              </span>
            </div>
          )}

          {/* Quantity Badge - "Only X Left" with blinking background (only for items with quantity) */}
          {!currentItem.hide_quantity && !currentItem.is_sold_out && (
            <div 
              className={`flex items-center justify-center gap-3 text-white px-14 py-8 rounded-3xl shadow-2xl ${urgency.glow} animate-slide-right ${urgency.blinkClass}`}
              style={{ animationDelay: '0.2s' }}
            >
              <span className="text-4xl font-semibold tracking-wide">Only</span>
              <span className="text-9xl font-black tracking-wide animate-qty-number">
                {urgency.qty}
              </span>
              <span className="text-4xl font-semibold tracking-wide">Left</span>
            </div>
          )}

          {/* Available Badge - For items without quantity tracking (breakfast/lunch) */}
          {currentItem.hide_quantity && (
            <div 
              className="flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-14 py-8 rounded-3xl shadow-2xl animate-slide-right"
              style={{ animationDelay: '0.2s' }}
            >
              <span className="text-5xl font-black tracking-wide">AVAILABLE NOW</span>
            </div>
          )}

          {/* Sold Out Badge (only for items with quantity) */}
          {!currentItem.hide_quantity && currentItem.is_sold_out && (
            <div 
              className="flex items-center justify-center bg-gray-600 text-white px-14 py-8 rounded-3xl shadow-2xl animate-slide-right"
              style={{ animationDelay: '0.2s' }}
            >
              <span className="text-6xl font-black tracking-wide">SOLD OUT</span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section - CTA Only (no carousel) */}
      <div className="py-4 px-8 shrink-0">
        {/* Call to Action - Large text across the page */}
        <p className="text-center text-3xl font-black text-amber-400 tracking-wide animate-fade-in">
          Come On In! Pick Up Yours Before We Run Out For The Day!
        </p>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fade-in { 0% { opacity: 0; } 100% { opacity: 1; } }
        @keyframes slide-right { 0% { opacity: 0; transform: translateX(-40px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes slide-up { 0% { opacity: 0; transform: translateY(40px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes scale-in { 0% { opacity: 0; transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes slow-zoom { 0% { transform: scale(1); } 100% { transform: scale(1.1); } }
        @keyframes pulse-slow { 0%, 100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.05); opacity: 1; } }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes float { 0%, 100% { transform: translateY(0) rotate(-5deg); } 50% { transform: translateY(-20px) rotate(5deg); } }
        
        /* Normal stock: Green to Dark Blue blink (1 second cycle) */
        @keyframes blink-normal {
          0%, 100% { background-color: #059669; } /* emerald-600 */
          50% { background-color: #1e3a5f; } /* dark blue */
        }
        
        /* Low stock (≤3): Red to Deep Orange blink (faster - 0.5 second cycle) */
        @keyframes blink-urgent {
          0%, 100% { background-color: #dc2626; } /* red-600 */
          50% { background-color: #ea580c; } /* orange-600 */
        }
        
        /* Selling Fast badge animation */
        @keyframes selling-fast {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        /* Quantity number pulse */
        @keyframes qty-number {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .animate-fade-in { animation: fade-in 0.6s ease-out; }
        .animate-slide-right { animation: slide-right 0.6s ease-out both; }
        .animate-slide-up { animation: slide-up 0.8s ease-out both; }
        .animate-scale-in { animation: scale-in 0.6s ease-out; }
        .animate-slow-zoom { animation: slow-zoom 7s ease-in-out forwards; }
        .animate-pulse-slow { animation: pulse-slow 3s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-blink-normal { animation: blink-normal 1s ease-in-out infinite; }
        .animate-blink-urgent { animation: blink-urgent 0.5s ease-in-out infinite; }
        .animate-selling-fast { animation: selling-fast 1s ease-in-out infinite; }
        .animate-qty-number { animation: qty-number 1s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default BakedGoodsDisplay;
